import { Link } from "react-router-dom";
import "./EmployeeSidebar.css";
import React, { useState, useEffect, useCallback } from "react";
import DefaultProfileImage from "../../../medias/images/default.jpg";
import { getTeamMembers } from "../../../services/wisdomServices/managerService";
import { requestGetImagesFromBucket } from "../../../services/wisdomServices/trackingService";
import { getSessionItem } from "../../../utils/helper/session";
import { InputSwitch } from "primereact/inputswitch";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { Sidebar } from "primereact/sidebar";
import UserCardTemplate from "../../../components/userCardTemplate/UserCardTemplate";
import { Can, PERMISSIONS } from "../../../security";

/**
 * @function EmployeeSidebar
 * @returns {JSX.Element} - employee sidebar component
 */
function EmployeeSidebar({ visible, onHide }) {
  const [items, setItems] = useState([]);
  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasNext, setHasNext] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loadedUsers, setLoadedUsers] = useState(0);
  const [disableSearch, setDisableSearch] = useState(true);
  const [disableSearchInfos, setDisableSearchInfos] = useState(false);
  const [employee, setEmployee] = useState("");
  const [loadingUserId, setLoadingUserId] = useState(null);

  const user = getSessionItem("User");

  /**
   * @function normalizeUserImage
   * @param {Object} data
   * @returns {Promise<Object>}
   * @description - normalizes user profile image source
   */
  const normalizeUserImage = async (data) => {
    const normalizedUser = { ...data };

    if (!normalizedUser.image) {
      normalizedUser.image = DefaultProfileImage;
      return normalizedUser;
    }

    if (normalizedUser.image.startsWith("data:image")) {
      return normalizedUser;
    }

    if (normalizedUser.image.startsWith("dataimage")) {
      normalizedUser.image = normalizedUser.image.replace(
        "dataimage/jpegbase64/",
        ""
      );
      normalizedUser.image = `data:image/jpeg;base64,${normalizedUser.image}`;
      return normalizedUser;
    }

    try {
      const blob = await requestGetImagesFromBucket(
        normalizedUser.id,
        normalizedUser.image,
        "profile"
      );

      if (blob && blob.size > 0) {
        normalizedUser.image = URL.createObjectURL(blob);
      } else {
        normalizedUser.image = DefaultProfileImage;
      }
    } catch (err) {
      console.error("Erro ao buscar imagem:", err);
      normalizedUser.image = DefaultProfileImage;
    }

    return normalizedUser;
  };

  /**
   * @function fetchImages
   * @param {Array<Object>} list
   * @returns {Promise<Array<Object>>}
   * @description - fetches and normalizes employee profile images
   */
  const fetchImages = async (list) => {
    return Promise.all((list || []).map(normalizeUserImage));
  };

  /**
   * @function loadUsers
   * @param {Object} options
   * @param {number} options.page
   * @param {boolean} options.append
   * @param {string|null} options.searchParam
   * @param {boolean} options.onlyDisabled
   * @description - generic loader for users with optional pagination/search
   */
  const loadUsers = useCallback(
    async ({
      page = 1,
      append = false,
      searchParam = null,
      onlyDisabled = disabled,
    } = {}) => {
      if (!user?.id) return;

      try {
        if (append) {
          setLoadingMore(true);
        } else {
          setLoading(true);
        }

        const resp = await getTeamMembers(
          user.id,
          onlyDisabled,
          searchParam,
          page,
          25
        );

        const normalizedList = await fetchImages(resp?.data || []);
        const pagination = resp?.pagination || {};

        setHasNext(Boolean(pagination.hasNextPage));
        setTotalUsers(pagination.totalResults || 0);
        setPageNumber(page);

        if (append) {
          setItems((prev) => {
            const nextItems = [...prev, ...normalizedList];
            setLoadedUsers(nextItems.length);
            return nextItems;
          });
        } else {
          setItems(normalizedList);
          setLoadedUsers(normalizedList.length);
        }
      } catch (error) {
        console.error("Erro ao buscar usuários:", error);
        if (!append) {
          setItems([]);
          setLoadedUsers(0);
          setTotalUsers(0);
          setHasNext(false);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [disabled, user?.id]
  );

  /**
   * @function resetAndLoadDefault
   * @description - resets list and loads first page without search
   */
  const resetAndLoadDefault = useCallback(async () => {
    setItems([]);
    setPageNumber(1);
    setHasNext(false);
    setLoadedUsers(0);
    setTotalUsers(0);
    setDisableSearchInfos(false);

    await loadUsers({
      page: 1,
      append: false,
      searchParam: null,
      onlyDisabled: disabled,
    });
  }, [disabled, loadUsers]);

  /**
   * @function toggleOnlyActive
   * @description - toggles active filter and resets pagination state
   */
  const toggleOnlyActive = () => {
    setDisabled((prev) => !prev);
    setItems([]);
    setHasNext(false);
    setPageNumber(1);
    setTotalUsers(0);
    setLoadedUsers(0);
  };

  /**
   * @function loadNextPage
   * @description - loads next page of employees
   */
  const loadNextPage = async () => {
    if (!hasNext || loadingMore) return;

    const nextPage = pageNumber + 1;

    await loadUsers({
      page: nextPage,
      append: true,
      searchParam: disableSearchInfos ? employee.trim() : null,
      onlyDisabled: disabled,
    });
  };

  /**
   * @function search
   * @description - executes employee search by name
   */
  const search = async () => {
    const searchTerm = employee.trim();

    if (searchTerm.length > 0 && searchTerm.length < 3) return;

    setItems([]);
    setPageNumber(1);
    setHasNext(false);
    setLoadedUsers(0);
    setTotalUsers(0);

    if (!searchTerm) {
      setDisableSearchInfos(false);
      await resetAndLoadDefault();
      return;
    }

    setDisableSearchInfos(true);

    await loadUsers({
      page: 1,
      append: false,
      searchParam: searchTerm,
      onlyDisabled: disabled,
    });
  };

  /**
   * @function handleKeyPress
   * @param {KeyboardEvent} event
   * @description - handles Enter key press for triggering search
   */
  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      search();
    }
  };

  /**
   * @function setEmployeeName
   * @param {string} value
   * @description - updates employee search input and controls search availability
   */
  const setEmployeeName = async (value) => {
    setEmployee(value);

    if (value.trim().length === 0) {
      setDisableSearch(true);
      await resetAndLoadDefault();
      return;
    }

    if (value.trim().length >= 3) {
      setDisableSearch(false);
    } else {
      setDisableSearch(true);
    }
  };

  useEffect(() => {
    if (!visible) return;
    resetAndLoadDefault();
  }, [visible, disabled, resetAndLoadDefault]);

  useEffect(() => {
    if (visible) {
      const scrollBarWidth =
        window.innerWidth - document.documentElement.clientWidth;

      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollBarWidth}px`;
    } else {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }

    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [visible]);

  return (
    <Sidebar
      visible={visible}
      onHide={onHide}
      position="right"
      className="user-search-sidebar"
      header={`Users: ${loadedUsers}/${totalUsers}`}
    >
      <div className="employee-sidebar-content">
        <div className="employee-sidebar-header">
          <div className="p-inputgroup flex-1 employee-sidebar-search-field">
            <InputText
              placeholder="Enter an employee name"
              value={employee}
              onKeyDown={handleKeyPress}
              onChange={(e) => setEmployeeName(e.target.value)}
            />
            <Can permission={PERMISSIONS.managersByIdTeamMembers_get}>
              <Button
                icon="pi pi-search"
                className="p-button-info"
                onClick={search}
                loading={loading}
                disabled={disableSearch}
              />

            </Can>
          </div>

          {!disableSearchInfos && (
            <div className="onlyActive">
              <label htmlFor="onlyActive">Only Active</label>
              <InputSwitch onChange={toggleOnlyActive} checked={!disabled} />
            </div>
          )}
        </div>

        <div className="user-list">
          {loading && !items.length ? (
            <div className="employee-sidebar-loading">
              <ProgressSpinner style={{ width: "40px", height: "40px" }} />
            </div>
          ) : items.length > 0 ? (
            items.map((employeeItem) => {
              const link = `/manager/summary/${employeeItem.id}`;

              return (
                <Link
                  to={link}
                  key={employeeItem.id}
                  style={{ textDecoration: "none" }}
                  onClick={() => setLoadingUserId(employeeItem.id)}
                >
                  <UserCardTemplate
                    user={employeeItem}
                    loading={loadingUserId === employeeItem.id}
                  />
                </Link>
              );
            })
          ) : (
            <div className="employee-sidebar-empty">
              No users found.
            </div>
          )}
        </div>

        {hasNext && !loading && (
          <div className="employee-sidebar-load-more">
            <Button
              label={loadingMore ? "Loading..." : "Load more"}
              icon={loadingMore ? null : "pi pi-angle-down"}
              onClick={loadNextPage}
              loading={loadingMore}
              outlined
            />
          </div>
        )}
      </div>
    </Sidebar>
  );
}

export default EmployeeSidebar;